import React, { useState, useEffect } from 'react';
import { Dialog, DialogTitle, DialogContent, IconButton, CircularProgress } from '@material-ui/core';
import { Close as CloseIcon, PhoneAndroid } from '@material-ui/icons';
import { createUploadSSESession, createShortLink } from 'Api/apiFunctions';
import QRCodeComponent from 'Utils/QRCodeComponent';
import styles from './MobileUploadQRModal.module.css';

const MobileUploadQRModal = ({ open, onClose }) => {
  const [sessionId, setSessionId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [shortUrl, setShortUrl] = useState('');
  const [copied, setCopied] = useState(false);

  const createSession = async () => {
    setLoading(true);
    setError(null);
    setShortUrl('');

    // Retry up to 3 times with short delay
    let id = null;
    for (let attempt = 1; attempt <= 3; attempt += 1) {
      try {
        console.log(`[MobileUploadQR] Creating SSE session (attempt ${attempt})...`);
        // eslint-disable-next-line no-await-in-loop
        const response = await createUploadSSESession();
        id = response?.data?.session_id;
        if (id) break;
      } catch (err) {
        console.warn(`[MobileUploadQR] Attempt ${attempt} failed:`, err);
        if (attempt < 3) {
          // eslint-disable-next-line no-await-in-loop
          await new Promise((r) => setTimeout(r, 1000));
        }
      }
    }

    if (!id) {
      setError('Failed to create session. Please try again.');
      setLoading(false);
      return;
    }

    console.log('[MobileUploadQR] Session created:', id);
    setSessionId(id);

    // Persist to localStorage so app.jsx SSE picks it up on refresh
    localStorage.setItem('mobileUploadSessionId', id);

    // Notify app.jsx to subscribe to SSE
    window.dispatchEvent(new CustomEvent('mobileUploadSessionCreated', { detail: { sessionId: id } }));

    // Shorten the QR URL (same as gift flow)
    const token = localStorage.getItem('token') || '';
    const uploaderToken = localStorage.getItem('uploader_token') || '';
    const path = `mobile-upload?session=${id}${token ? `&token=${token}` : ''}${uploaderToken ? `&access=${uploaderToken}` : ''}`;
    try {
      const shortRes = await createShortLink({ token, path });
      if (shortRes?.data?.link) {
        setShortUrl(shortRes.data.link);
      }
    } catch (shortErr) {
      console.warn('[MobileUploadQR] Short link failed, using full URL:', shortErr);
    }

    setLoading(false);
  };

  useEffect(() => {
    if (!open) return;

    let cancelled = false;
    const init = async () => {
      await createSession();
    };
    init().then(() => {
      if (cancelled) {
        // If modal closed before session was created, clean up
      }
    });

    return () => {
      cancelled = true;
    };
  }, [open]); // eslint-disable-line

  const handleClose = () => {
    // Don't clear session on close — SSE stays active so desktop keeps receiving
    // User can close modal and still get uploads from phone
    console.log('[MobileUploadQR] Modal closed. SSE session remains active:', sessionId);
    setSessionId(null);
    setError(null);
    setShortUrl('');
    setCopied(false);
    onClose();
  };

  const qrUrl = shortUrl
    || (sessionId
      ? `${window.location.origin}/mobile-upload?session=${sessionId}${localStorage.getItem('token') ? `&token=${localStorage.getItem('token')}` : ''}${localStorage.getItem('uploader_token') ? `&access=${localStorage.getItem('uploader_token')}` : ''}`
      : '');

  const handleCopyLink = async () => {
    if (!qrUrl) return;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(qrUrl);
      } else {
        // Fallback for insecure contexts / older browsers
        const temp = document.createElement('textarea');
        temp.value = qrUrl;
        temp.style.position = 'fixed';
        temp.style.opacity = '0';
        document.body.appendChild(temp);
        temp.select();
        document.execCommand('copy');
        document.body.removeChild(temp);
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.warn('[MobileUploadQR] Copy failed:', err);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="xs"
      fullWidth
      classes={{ paper: styles.dialogPaper }}
    >
      <DialogTitle className={styles.dialogTitle}>
        <div className={styles.titleRow}>
          <PhoneAndroid className={styles.titleIcon} />
          <span>Upload From Phone</span>
        </div>
        <IconButton onClick={handleClose} className={styles.closeButton} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent className={styles.dialogContent}>
        {loading && (
          <div className={styles.centered}>
            <CircularProgress size={40} />
            <p className={styles.statusText}>Creating session...</p>
          </div>
        )}
        {error && (
          <div className={styles.centered}>
            <p className={styles.errorText}>{error}</p>
            <button
              type="button"
              className={styles.retryButton}
              onClick={createSession}
            >
              Try Again
            </button>
          </div>
        )}
        {!loading && !error && sessionId && (
          <div className={styles.centered}>
            <div className={styles.qrContainer}>
              <QRCodeComponent url={qrUrl} size={200} />
            </div>
            <p className={styles.instruction}>
              Scan with your phone camera to upload files from your mobile device
            </p>

            {qrUrl && (
              <div className={styles.copyLinkWrap}>
                <span className={styles.copyLinkLabel}>Or copy the link</span>
                <div className={styles.copyLinkRow}>
                  <input
                    type="text"
                    className={styles.copyLinkInput}
                    value={qrUrl}
                    readOnly
                    onFocus={(e) => e.target.select()}
                  />
                  <button
                    type="button"
                    className={styles.copyLinkButton}
                    onClick={handleCopyLink}
                  >
                    {copied ? 'Copied!' : 'Copy'}
                  </button>
                </div>
              </div>
            )}

            <p className={styles.hint}>
              Files will appear in the upload panel as they are uploaded
            </p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default MobileUploadQRModal;
